import {
  createAsyncThunk,
  createEntityAdapter,
  createSlice,
  Dictionary,
  EntityId,
  EntityState,
} from '@reduxjs/toolkit';
import { apiClient } from 'api';
import {
  CreateOrderData,
  GetOrderList,
  UpdateOrderData,
} from 'api/order';
import {
  Order,
} from 'constants/interface';
import { OrderApiStatus } from 'constants/order';
import { RootState } from 'state';

const orderEntityAdapter = createEntityAdapter<Order>();

export interface OrdersState extends EntityState<Order> {
  isLoading: boolean
  remoteTotal: number
}

export const initialState: OrdersState = orderEntityAdapter.getInitialState({
  isLoading: false,
  remoteTotal: 0,
});

export const createOrderAction = createAsyncThunk(
  'orders/create',
  async (payload: CreateOrderData) => {
    const orderData = await apiClient.order.createOrder(payload);
    return orderData;
  },
);

export const updateOrderAction = createAsyncThunk(
  'orders/update',
  async (payload: UpdateOrderData) => {
    const orderData = await apiClient.order.updateOrder(payload);
    return orderData;
  },
);

export const getOrderAction = createAsyncThunk(
  'orders/get',
  async (payload: {
    orderId: string,
  }) => {
    const { orderId } = payload;
    const response = await apiClient.order.getOrder(orderId);

    return response;
  },
);

export const getOrderListAction = createAsyncThunk(
  'orders/list',
  async (payload: GetOrderList) => {
    const response = await apiClient
      .order
      .getOrderList(payload);

    return response;
  },
);

export const setOrderStatusAction = createAsyncThunk(
  'orders/setStatus',
  async (payload: {
    orderId: string,
    status: OrderApiStatus,
  }) => {
    const {
      orderId,
      status,
    } = payload;
    const orderData = await apiClient.order.setOrderStatus(orderId, status);
    return orderData;
  },
);

export const ordersSlice = createSlice({
  name: 'orders',
  initialState,
  reducers: {
    cleanOrdersAction: orderEntityAdapter.removeAll,
  },
  extraReducers: (builder) => {
    // List
    builder.addCase(getOrderListAction.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(getOrderListAction.fulfilled, (state, action) => {
      orderEntityAdapter.removeAll(state);
      orderEntityAdapter.addMany(state, action.payload.data);
      state.remoteTotal = action.payload.pagination.total;
      state.isLoading = false;
    });
    builder.addCase(getOrderListAction.rejected, (state) => {
      state.isLoading = false;
    });

    // Single
    builder.addCase(getOrderAction.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(getOrderAction.fulfilled, (state, action) => {
      // orderEntityAdapter.removeAll(state);
      // orderEntityAdapter.addMany(state, action.payload.data);
      orderEntityAdapter.removeOne(state, action.payload.id);
      orderEntityAdapter.addOne(state, action.payload);
      state.isLoading = false;
    });
    builder.addCase(getOrderAction.rejected, (state) => {
      state.isLoading = false;
    });

    // // Status
    // builder.addCase(setOrderStatusAction.pending, (state) => {
    //   state.isLoading = true;
    // });
    // builder.addCase(setOrderStatusAction.fulfilled, (state, action) => {
    //   orderEntityAdapter.removeOne(state, action.payload.id);
    //   orderEntityAdapter.addOne(state, action.payload);
    //   state.isLoading = false;
    // });
    // builder.addCase(setOrderStatusAction.rejected, (state) => {
    //   state.isLoading = false;
    // });
  },
});

export const {
  cleanOrdersAction,
} = ordersSlice.actions;

// Selectors

export const isLoadingOrdersSelector = (state: RootState):
  boolean => state.orders.isLoading;

export const ordersEntitiesSelector = (state: RootState):
  Dictionary<Order> => state.orders.entities;

export const ordersIdsSelector = (state: RootState):
  EntityId[] => state.orders.ids;

export const remoteTotalSelector = (state: RootState):
  number => state.orders.remoteTotal;
